import { useCallback, useMemo, useRef } from 'react';
import { TouchBackend } from 'react-dnd-touch-backend';
import { DndProvider } from 'react-dnd';
import { ContentContainerType } from 'types/enums';
import { SoundCloudVersion } from 'types/releases';
import {
  getOpenInNewTabMenuOption,
  useContextMenu,
} from 'app/components/ChakraTableList';
import { ReactComponent as AudioIcon } from 'resources/images/audio.svg';
import { ChakraEmptyList } from 'app/components/Core';
import { ListDragLayer } from 'app/components/DraggableListRow';
import { GenericObject } from 'types/generic.types';
import { routes } from 'app/routing/routesConfig';
import { ContentContainer } from 'app/components/Core/ChakraContentContainer';
import { useVersionsListReorder } from 'app/components/Releases/hooks';
import { Row } from '@tanstack/react-table';
import { contextMenuHandler } from 'app/components/Table/ContextMenuButton/utils';
import { StickyTable } from 'app/components/Core/StickyTable/StickyTable';
import { useTotalWidth } from 'app/components/Core/StickyTable';
import { ReleaseVersionTableRow } from './ReleaseVersionTableRow';
import { getColumns } from './utils';

export interface ReleaseVersionsListComponentProps {
  loading: boolean;
  versions: SoundCloudVersion[];
  canDeleteRelease: boolean;
  releaseGuid: string;
  onDelete: (data: {
    versionLabel?: string;
    versionGuid: string;
    songGuid: string;
  }) => void;
  currentlyReplacingAudio: GenericObject<boolean>;
}
export const ReleaseVersionsListComponent = ({
  versions,
  loading,
  canDeleteRelease,
  releaseGuid,
  onDelete,
  currentlyReplacingAudio,
}: ReleaseVersionsListComponentProps) => {
  const {
    tooltipTitle,
    rows,
    onRowReorder,
    onDragging,
  } = useVersionsListReorder(versions);

  const contextMenuProps = useContextMenu();

  const rowRenderer = useCallback(
    (row: Row<SoundCloudVersion>, index) => {
      const version = row.original;
      const { song, title } = version;

      const versionDetailsPath = routes.releaseVersionDetails.path
        .replace(':guid', releaseGuid)
        .replace(':versionGuid', version.guid);

      const options = [
        ...getOpenInNewTabMenuOption(versionDetailsPath),
        ...(canDeleteRelease
          ? [
              {
                title: 'Delete',
                value: 'DELETE',
                onClick: () => {
                  onDelete({
                    versionLabel: title || song.title,
                    versionGuid: version.guid,
                    songGuid: song.guid,
                  });
                },
              },
            ]
          : []),
      ];

      return (
        <ReleaseVersionTableRow
          key={row.id}
          row={row}
          contextHandler={(e) =>
            contextMenuHandler(e, contextMenuProps, options)
          }
          index={index}
          currentlyReplacingAudio={currentlyReplacingAudio}
          onRowReorder={onRowReorder}
          onDragging={onDragging}
          href={versionDetailsPath}
        />
      );
    },
    [
      canDeleteRelease,
      contextMenuProps,
      currentlyReplacingAudio,
      onDelete,
      onDragging,
      onRowReorder,
      releaseGuid,
    ]
  );

  const ref = useRef<HTMLDivElement>(null);
  const tableKey = 'release-versions';
  const totalWidth = useTotalWidth(ref);
  const columns = useMemo(() => getColumns(totalWidth), [totalWidth]);

  if (!loading && rows.length === 0) {
    return (
      <ChakraEmptyList
        icon={<AudioIcon role="img" />}
        title="No audio files added"
        primaryText="Add an audio file to your Release to fetch all necessary song info and credits."
      />
    );
  }

  return (
    <ContentContainer type={ContentContainerType.LIST} isViewContent>
      <DndProvider backend={TouchBackend} options={{ enableMouseEvents: true }}>
        <StickyTable<SoundCloudVersion>
          tableKey={tableKey}
          ref={ref}
          data={rows}
          columns={columns}
          rowRenderer={rowRenderer}
          contextMenuProps={contextMenuProps}
        />
        <ListDragLayer title={tooltipTitle || ''} />
      </DndProvider>
    </ContentContainer>
  );
};
